/**
 * What every user event says about the page it happened on, and the sign of
 * life this module sends back when no other event does.
 *
 * The server knows a page was RENDERED. Only the browser knows whether anyone
 * stayed on it, how far down they got, and whether our script ran at all — so
 * that half of the record has to be measured here and reported. Everything the
 * report carries is collected in one place and handed out through
 * `getUserEventState()`, so no other module has to know how any of it is
 * measured.
 *
 * TWO CLOCKS, NOT ONE
 *
 * `wall_milliseconds` runs from the moment the page loaded and never stops.
 * `engaged_milliseconds` only accumulates while `document.visibilityState` is
 * `visible`. They answer different questions and neither one substitutes for
 * the other: wall time alone makes a tab left open overnight look like deep
 * reading, while active time alone loses "they came back to this tab five
 * times over an hour", which is the shape of someone comparing products.
 *
 * WHAT THE VISIBILITY SIGNAL DOES NOT SEE
 *
 * `visibilitychange` fires when the tab is switched away from and when the
 * window is minimised. It does NOT fire when our window is simply covered by
 * another window — that reader counts as engaged for as long as the tab stays
 * selected. We deliberately do NOT add `blur` / `focus` to close the gap:
 * those fire when the reader clicks the address bar, opens devtools or picks
 * up a different window for a second, and counting any of that as leaving the
 * page would cost more accuracy than the covered-window case costs us. The gap
 * is known and accepted, and it is recorded here rather than in a document
 * beside the code, because this is where somebody would otherwise "fix" it.
 *
 * THE SCREEN AND THE WINDOW
 *
 * Two measurements, two questions, and they used to travel in two cookies read
 * off every request — which meant the visit ended up holding whatever the
 * window was on the way OUT, and no single render held anything at all. They
 * ride the beacon now, and the server decides which row keeps which reading:
 * the first measurement of a visit describes the DEVICE and lands on the visit,
 * every later one describes a PAGE and lands on that page's engagement row
 * (`TRACK-SESSION-028`).
 *
 * `resolution` is the physical screen. It identifies the KIND of device and has
 * years of history behind it, so it keeps both its name and its meaning —
 * redefining it would blend two things into one column and make every
 * comparison across that boundary wrong.
 *
 * `viewport` is the browser WINDOW, and it is the one that decides what the
 * visitor actually saw: the layout reacts to the window, never to the monitor.
 * Somebody on a 1920px screen with a half-width window gets the tablet layout,
 * and under the screen measurement alone they are indistinguishable from
 * somebody on a full-screen desktop.
 *
 * `documentElement.clientWidth` rather than `window.innerWidth`: the latter
 * counts the scrollbar, so a desktop sitting a few pixels above a breakpoint
 * reports the wrong side of it.
 *
 * Sent only when the reading is NEW — first signal of the page, or the window
 * changed since the last one. The old cookie needed a debounced `resize`
 * listener because every write was carried by the next request; here the value
 * is re-read at send time and compared, so a drag costs nothing and the next
 * scheduled signal reports where it stopped.
 *
 * HOW LONG THE PAGE TOOK TO LOAD
 *
 * Six segments of one navigation, cut where the browser publishes a mark:
 * network, server, transfer, DOM processing, DOM completion, `load`. They are
 * measured here because nothing else can measure them — the server knows how
 * long IT took and nothing about the connection, the parse or the visitor's
 * device — and they are reported to our own row FIRST, so "how long does the
 * shop take to load, for whom, at what hour, in which country, on what class
 * of device" is answered from our own database, where every one of those
 * dimensions already sits on the visit. Matomo gets the same six as a second
 * consumer, not as the place the answer lives.
 *
 * Taken once per page view and only AFTER the `load` event, because before it
 * the later marks are still zero and a reading taken then would report a page
 * that finished loading instantly. The first scheduled signal goes out five
 * seconds in, usually after `load` and sometimes not, so the measurement is
 * offered to every signal and taken by whichever one is the first to find it
 * complete.
 *
 * All six travel together or none does. Measured on the Matomo test site: the
 * page-load total is the SUM of the six columns, so one missing segment makes
 * the whole total null — five accepted values and a gap report nothing at all,
 * while a genuine zero (a page with no `load` handlers) reports correctly.
 *
 * A navigation out of history (`back_forward`) and a page restored from the
 * back/forward cache are both skipped. Neither one performed the navigation it
 * would be reporting: the marks left on the entry describe the ORIGINAL load,
 * minutes or hours earlier, and a restore leaves no new marks at all. Sending
 * them would file somebody else's page load against this render.
 */

// Same endpoint as the event beacon — deliberately named after nothing, so a
// content blocker has no word to match on. The rule binds the URL and not this
// file's name: modules are bundled into `app.js`, so nothing here is ever
// fetched under a name a blocker could match.
const STORE_EVENT_URL = '/xhr/common/set-state/';

// Matches `tracking.xhr_views.PULSE_EVENT`. The signal records no event row of
// its own on the server; it folds into the one page-view row and returns.
const PULSE_EVENT = 'page_pulse';

// The first signal comes early, because the interesting question it answers —
// "did a browser actually run our script on this render?" — is worth answering
// before most visitors have left. It is also the only signal 85% of renders
// ever get, and the one the server's engagement floor is decided on, so it
// stays where it is.
const FIRST_SIGNAL_MS = 5000;

// The spacing between the signals after it: a ladder climbed one rung per
// signal, and past its top every five minutes. Dense at the start, where most
// visits end and a reading is worth the most, sparse once the page has been
// open for minutes. The first rung is also how soon a pulse that went quiet
// speaks again once the visitor is back.
const SIGNAL_LADDER_MS = [10000, 15000, 30000, 60000, 120000, 240000];
const SIGNAL_RESUME_MS = SIGNAL_LADDER_MS[0];

// Every signal is spaced further from the last, up to a ceiling. The numbers
// the pulse carries — active time, wall time, deepest scroll — are MONOTONIC
// and folded with `Greatest` on the server, so only the last one of a page view
// is ever read: the ones in between buy nothing except the resolution at which
// we would still know something if the farewell signal never arrived. Spacing
// them out costs that resolution and nothing else.
//
// Measured on production over 7 days: 0,26% of renders (485 of 185 690) sent
// 41 beacons or more and produced 25% of all beacon traffic; one tab sent
// 4 917. Those are not readers — they are tabs left open, and the reason they
// kept talking is that the active clock kept running (see IDLE_AFTER_MS).
const SIGNAL_MAX_INTERVAL_MS = 5 * 60 * 1000;

// How long the active clock keeps running after the last thing the visitor
// did. Scrolling, moving the pointer, typing, tapping — anything a person
// produces and a script does not.
//
// The clock used to run from "tab visible" to "tab hidden", which measured the
// wrong thing: a window left open on a second monitor fires no
// `visibilitychange` and was recorded as an hour of reading. A minute is the
// longest a visitor plausibly spends on one screenful of a catalogue page
// without moving anything, and it is the industry's usual figure — short
// enough that an abandoned tab stops counting quickly, long enough that a slow
// reader is not cut off mid-paragraph.
const IDLE_AFTER_MS = 60 * 1000;

// `scroll` is not in the list: it has its own listener, which reads the depth
// as well as noting the act.
const INTERACTION_EVENTS = ['pointermove', 'pointerdown', 'keydown', 'wheel'];

// A page being scrolled fires `scroll` hundreds of times a second. 200 ms is
// below the granularity of anything we report (whole percent of the document)
// and far above the event rate.
const SCROLL_THROTTLE_MS = 200;

// Mirrors `tracking.abstracts.ConsentDecision`. Used only as the fallback for
// a page where the consent module has not published its state yet.
const DECISION_UNDECIDED = 'undecided';

let pageLoadedAt = Date.now();
let engagedMilliseconds = 0;
// Timestamp the tab last became visible, or `null` while it is hidden. Keeping
// the open interval separate from the accumulator is what lets the reported
// active time be exact at any moment without a ticking timer.
let engagedSince = null;

let scrollPercent = 0;
let maxScrollPercent = 0;
let matomoTagLoaded = false;

// The last screen and window actually put on the wire, so an unchanged reading
// is not repeated. `null` until the first beacon, which is what makes the first
// beacon of every page carry one.
let reportedDisplay = null;

// Whether the six load segments have already been handed to a beacon. One
// reading per render, so this is a latch and not a comparison — unlike the
// window, the numbers describe a moment that has passed and cannot change.
let reportedPerformance = false;

// Raised when this document came back out of the back/forward cache. The
// restore runs no navigation, so the marks on the navigation entry still
// describe the original load and reporting them here would attribute it twice.
let restoredFromHistory = false;

// When the visitor last did something. Seeded with the page load, because
// arriving on a page IS an act — a reader who lands and reads a screenful
// without touching anything is engaged, and stays so for `IDLE_AFTER_MS`.
let lastInteractionAt = 0;

let started = false;
let firstSignalTimeout = null;
let signalTimeout = null;
// The rung of `SIGNAL_LADDER_MS` the NEXT signal is spaced by. Climbs one per
// signal and never comes back down within a page view.
let signalStep = 0;
// Raised when a signal found nothing to say and therefore did not arm the next
// one. An interaction is what starts the rhythm again — see `noteInteraction`.
let signalPaused = false;
let scrollThrottleTimeout = null;

// What the last signal said, so a silent clock has nothing to report. Wall time
// is excluded on purpose: it grows on its own and would make every state look
// changed, which is precisely the case this guard exists to catch.
let lastReported = null;

/**
 * How far down the document the reader currently is, in whole percent.
 *
 * A page that fits in the window reports 100: there is nothing below the fold,
 * so the whole document is as read as it can be. Reporting 0 there would file
 * every short page as bounced-at-the-top.
 */
function calcScrollPercent() {
    const element = document.documentElement;
    if (!element) {
        return 0;
    }
    const viewportHeight = window.innerHeight || element.clientHeight || 0;
    const scrollable = (element.scrollHeight || 0) - viewportHeight;
    if (scrollable <= 0) {
        // Nothing to scroll — which is NOT "scrolled to the end". Returning 100
        // here poisoned the high-water mark for the whole render: the first
        // reading is taken before the layout has grown to its full height, so
        // `maxScrollPercent` latched 100 and never came down. On production
        // 11 816 of 11 821 rows held exactly 100 and the column carried no
        // information at all. `null` means unmeasurable, and the caller leaves
        // the mark alone.
        return null;
    }
    const offset = window.scrollY !== undefined ? window.scrollY : element.scrollTop || 0;
    return Math.max(0, Math.min(100, Math.round((offset / scrollable) * 100)));
}

/**
 * Take a scroll reading and raise the high-water mark.
 *
 * Called both from the throttled listener and from `getUserEventState()`. The
 * listener is what catches a reader who goes to the bottom and comes back up
 * between two signals — without it the maximum would only ever see wherever
 * they happened to be when a signal fired.
 */
function readScroll() {
    const reading = calcScrollPercent();
    if (reading === null) {
        // Unmeasurable right now — a page that has not grown yet, or one that
        // genuinely fits the window. Neither is a reading, so the previous one
        // stands and the mark is untouched.
        return;
    }

    scrollPercent = reading;
    if (reading > maxScrollPercent) {
        maxScrollPercent = reading;
    }
}

/**
 * Active time as of now, including the interval currently open.
 */
function calcEngagedMilliseconds() {
    const open = engagedSince === null ? 0 : engagedUntil(Date.now()) - engagedSince;
    return Math.max(0, engagedMilliseconds + Math.max(0, open));
}

/**
 * The moment the currently open interval of attention ends: now, or the point
 * the visitor went idle, whichever came first.
 *
 * No timer is needed to notice idleness — the deadline is a function of the
 * last interaction, so every reader of the clock arrives at the same answer
 * whenever it is asked.
 */
function engagedUntil(now) {
    return Math.min(now, lastInteractionAt + IDLE_AFTER_MS);
}

/**
 * Bank the open interval and stop the clock, crediting only the part of it the
 * visitor was actually there for.
 */
function stopEngagedClock(now) {
    if (engagedSince === null) {
        return;
    }
    engagedMilliseconds += Math.max(0, engagedUntil(now) - engagedSince);
    engagedSince = null;
}

/**
 * Something the visitor did.
 *
 * Two jobs. It moves the idle deadline, and — if the clock had already stopped
 * at the previous deadline — it closes that interval before opening a new one,
 * so the silence in between is never credited backwards. And it wakes a pulse
 * that went quiet: somebody who starts scrolling again after a pause is doing
 * something, and the next reading should be soon rather than never.
 */
function noteInteraction() {
    const now = Date.now();
    const wasIdle = now > lastInteractionAt + IDLE_AFTER_MS;

    if (wasIdle && engagedSince !== null) {
        stopEngagedClock(now);
        engagedSince = now;
    }

    lastInteractionAt = now;

    // Only a pulse that went QUIET is woken, and it resumes where the ladder
    // stood — an interaction does not climb it back down. Until 2026-09-30
    // every scroll and pointer move reset the spacing to fifteen seconds, so a
    // visitor reading actively never left the bottom rung: ~25 signals on a
    // seven-minute page, and the pulse was the larger half of all requests to
    // the application (~15 000 an hour against ~3 000 page views). The numbers
    // it carries are monotonic and the farewell signal goes out when the tab is
    // hidden and on `pagehide`, so a climbing ladder costs only the resolution
    // of a page whose browser died mid-visit.
    if (signalPaused) {
        signalPaused = false;
        scheduleSignal(SIGNAL_RESUME_MS);
    }
}

/**
 * Everything known about this page view, in the wire vocabulary the beacon
 * endpoint reads. Booleans travel as `'1'` / `'0'`, which is the spelling the
 * server's `BeaconPayload.read_boolean` accepts.
 *
 * The page-view id is read from the DOM at call time rather than captured at
 * load: this module is initialised from `app.js`, which runs in `<head>`,
 * before `<body>` and its attributes exist.
 */
export function getUserEventState() {
    readScroll();
    const body = document.body;
    const consentState = window.consentState;
    return {
        page_view_id: (body && body.dataset.pageViewId) || '',
        engaged_milliseconds: calcEngagedMilliseconds(),
        wall_milliseconds: Math.max(0, Date.now() - pageLoadedAt),
        scroll_percent: scrollPercent,
        max_scroll_percent: maxScrollPercent,
        matomo_tag_loaded: matomoTagLoaded ? '1' : '0',
        consent_decision: (consentState && consentState.decision) || DECISION_UNDECIDED,
    };
}

/**
 * The screen and the window as they are RIGHT NOW, but only if that is news.
 *
 * A taking rather than a reading, and the name says so: the values are marked
 * as reported by the act of returning them, so the next call answers with
 * nothing until the window changes. That is why it is not part of
 * `getUserEventState()` — that one is called to compare states as well as to
 * send them, and a getter that consumed what it returned would drop a
 * measurement every time a signal was weighed and not sent.
 *
 * Empty object = nothing new. The server reads an absent parameter as "the page
 * did not say", which is exactly right here.
 */
export function takeDisplayMeasurement() {
    const resolution = screen.width + 'x' + screen.height;
    const width = document.documentElement.clientWidth || window.innerWidth;
    const height = document.documentElement.clientHeight || window.innerHeight;
    const viewport = width + 'x' + height;

    if (reportedDisplay
            && reportedDisplay.resolution === resolution
            && reportedDisplay.viewport === viewport) {
        return {};
    }
    reportedDisplay = {resolution, viewport};
    return {resolution, viewport};
}

/**
 * One segment of a load, in whole milliseconds and never below zero.
 *
 * A browser that never reached a mark leaves it at zero, which makes the
 * difference negative for every segment after it. Zero is the honest answer
 * there — the segment did not happen — and it is also why the caller refuses
 * to report anything at all until `load` has fired.
 */
function segmentMilliseconds(value) {
    return Math.max(0, Math.round(value || 0));
}

/**
 * The six segments cut out of one set of navigation marks.
 *
 * `navigationStart` is passed in rather than read here because the two
 * interfaces anchor time differently: the modern entry counts from its own
 * `startTime` (zero), the legacy one from an absolute epoch. Everything else
 * is a difference between two marks and reads the same on both.
 *
 * DOM processing is measured from `responseEnd` rather than from the legacy
 * `domLoading`, which the modern entry does not publish at all — the two marks
 * name the same instant, and using the one both interfaces have keeps the
 * number comparable between browsers instead of only within one of them.
 */
function buildLoadSegments(timing, navigationStart) {
    return {
        network_milliseconds: segmentMilliseconds(timing.connectEnd - navigationStart),
        server_milliseconds: segmentMilliseconds(timing.responseStart - timing.requestStart),
        transfer_milliseconds: segmentMilliseconds(timing.responseEnd - timing.responseStart),
        dom_processing_milliseconds: segmentMilliseconds(timing.domInteractive - timing.responseEnd),
        dom_completion_milliseconds: segmentMilliseconds(timing.domComplete - timing.domInteractive),
        onload_milliseconds: segmentMilliseconds(timing.loadEventEnd - timing.loadEventStart),
    };
}

/**
 * This navigation's marks, or `null` when there is nothing true to report yet.
 *
 * Four reasons to answer with nothing, and every one of them would otherwise
 * produce a number that looks like a measurement:
 *
 * * no `performance` object at all — nothing to read;
 * * `load` has not fired (`loadEventEnd` still zero), so the last segments
 *   have not happened and reporting now would file the page as instant;
 * * the navigation came out of session history (`back_forward`), where the
 *   marks describe the original load rather than this one;
 * * the browser did not say when the request left (`requestStart` zero, as a
 *   cache-served navigation can leave it). Without that mark the network and
 *   the server cannot be told apart, and on the legacy interface — whose marks
 *   are absolute timestamps — the arithmetic produces the epoch instead of a
 *   duration.
 */
function readNavigationTiming() {
    const timeline = window.performance;
    if (!timeline) {
        return null;
    }
    const entry = timeline.getEntriesByType
        ? timeline.getEntriesByType('navigation')[0]
        : null;
    if (entry) {
        if (entry.type === 'back_forward' || !entry.loadEventEnd || !entry.requestStart) {
            return null;
        }
        return buildLoadSegments(entry, entry.startTime || 0);
    }
    const legacy = timeline.timing;
    if (!legacy || !legacy.loadEventEnd || !legacy.requestStart || !legacy.navigationStart) {
        return null;
    }
    // `PerformanceNavigation.TYPE_BACK_FORWARD` is 2 — the legacy spelling of
    // the check above.
    if (timeline.navigation && timeline.navigation.type === 2) {
        return null;
    }
    return buildLoadSegments(legacy, legacy.navigationStart);
}

/**
 * How long this page took to load, in six segments, or `{}` when there is
 * nothing to say.
 *
 * A taking rather than a reading, the same shape as `takeDisplayMeasurement()`
 * and for the same reason: returning the values marks them as reported, so the
 * measurement rides exactly one beacon per render instead of every one of
 * them. It is offered to each signal because the first one is scheduled five
 * seconds in and `load` is not guaranteed to have fired by then — whichever
 * signal finds the marks complete is the one that carries them.
 *
 * Empty object = nothing new, which the server reads as "the page did not
 * say". That is the right answer for a page still loading, for a restore out
 * of the back/forward cache, and for every signal after the first one that
 * reported.
 */
export function takePerformanceMeasurement() {
    if (reportedPerformance || restoredFromHistory) {
        return {};
    }
    const segments = readNavigationTiming();
    if (!segments) {
        return {};
    }
    reportedPerformance = true;
    return segments;
}

/**
 * Record that the Matomo container actually started on this page view.
 *
 * The server dispatches the page view to Matomo itself when the browser never
 * managed to; this flag is what tells the two apart, so it is only ever raised
 * by the code that does the loading.
 */
export function setMatomoTagLoaded() {
    matomoTagLoaded = true;
}

/**
 * POST one signal. `extraParams` are appended last, so a caller that states a
 * value explicitly overrides the measured one — Django's `POST.get` keeps the
 * last of a repeated key.
 *
 * This does not go through `event-store.js`: that module imports THIS one to
 * attach the state to its own payloads, and importing back would close the
 * cycle. The cost is one small `fetch` written twice; the alternative is a
 * circular import between the measurement and the thing being measured.
 */
function post(extraParams) {
    const state = getUserEventState();
    if (!state.page_view_id) {
        // Nothing to attach the reading to. The server rejects a beacon with no
        // page-view id anyway, so this is a request saved rather than a rule
        // enforced twice.
        return;
    }
    const body = new FormData();
    body.append('event', PULSE_EVENT);
    Object.keys(state).forEach((key) => body.append(key, state[key]));
    const display = takeDisplayMeasurement();
    Object.keys(display).forEach((key) => body.append(key, display[key]));
    const loadSegments = takePerformanceMeasurement();
    Object.keys(loadSegments).forEach((key) => body.append(key, loadSegments[key]));
    Object.keys(extraParams || {}).forEach((key) => {
        const value = extraParams[key];
        if (value === undefined || value === null || value === '') return;
        body.append(key, value);
    });
    lastReported = state;
    fetch(STORE_EVENT_URL, {
        method: 'POST',
        headers: {'X-CSRFToken': (document.body && document.body.dataset.csrfToken) || ''},
        body,
        // The last signal of a page view is sent while the page is going away.
        keepalive: true,
    }).catch(() => {});
}

/**
 * Whether anything worth reporting has happened since the last signal. Wall
 * time is not in the comparison — see `lastReported`.
 */
function hasNews(state) {
    if (!lastReported) {
        return true;
    }
    return state.engaged_milliseconds !== lastReported.engaged_milliseconds
        || state.scroll_percent !== lastReported.scroll_percent
        || state.max_scroll_percent !== lastReported.max_scroll_percent
        || state.matomo_tag_loaded !== lastReported.matomo_tag_loaded
        || state.consent_decision !== lastReported.consent_decision;
}

/**
 * The scheduled signal. A hidden tab whose clocks are all frozen has nothing
 * to say, so it says nothing — the tab left open in the background would
 * otherwise keep a request every minute going for as long as it exists.
 */
function sendSignal() {
    if (!hasNews(getUserEventState())) {
        // Nothing moved since the last one, so there is nothing to say —
        // whatever the tab is doing. The guard used to require the tab to be
        // hidden as well, which left the case it was written for uncovered: a
        // VISIBLE tab nobody is looking at kept reporting, because the active
        // clock kept running. It no longer does (see `IDLE_AFTER_MS`), so this
        // condition now catches an abandoned window too.
        signalPaused = true;
        return false;
    }
    post();
    return true;
}

/**
 * Arm the next signal, and only the next one.
 *
 * A re-arming `setTimeout` rather than `setInterval`, because the spacing
 * changes: it doubles after every signal and an interaction puts it back.
 */
function scheduleSignal(delay) {
    if (signalTimeout !== null) {
        clearTimeout(signalTimeout);
    }
    signalTimeout = setTimeout(() => {
        signalTimeout = null;
        if (!sendSignal()) {
            // Silent: the timer stops here rather than waking up to find the
            // same silence. `noteInteraction` starts it again.
            return;
        }
        scheduleSignal(nextSpacing());
    }, delay);
}

/**
 * The spacing to the next signal, climbing the ladder by one rung; past its
 * top, the five-minute ceiling for as long as the page stays open.
 */
function nextSpacing() {
    const spacing = signalStep < SIGNAL_LADDER_MS.length
        ? SIGNAL_LADDER_MS[signalStep]
        : SIGNAL_MAX_INTERVAL_MS;
    signalStep += 1;
    return spacing;
}

/**
 * Send a signal immediately, whatever the schedule was going to do.
 *
 * Used by the consent banner: a click on it is the earliest proof there is
 * that somebody is on the page and that our script is running.
 */
export function sendNow(extraParams) {
    post(extraParams);
}

function handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
        // Coming back to a tab IS an act, and the one that proves the visitor
        // is here — so it moves the idle deadline before the clock restarts,
        // or the restarted interval would be credited against a deadline that
        // has already passed.
        noteInteraction();
        if (engagedSince === null) {
            engagedSince = Date.now();
        }
        return;
    }
    stopEngagedClock(Date.now());
    // A tab switched away from may never be looked at again, so what was
    // measured up to here is reported now rather than at the next tick.
    post();
}

function handleScroll() {
    noteInteraction();
    if (scrollThrottleTimeout !== null) {
        return;
    }
    scrollThrottleTimeout = setTimeout(() => {
        scrollThrottleTimeout = null;
        readScroll();
    }, SCROLL_THROTTLE_MS);
}

export function init() {
    if (started) {
        return;
    }
    started = true;

    pageLoadedAt = Date.now();
    engagedMilliseconds = 0;
    engagedSince = document.visibilityState === 'hidden' ? null : Date.now();
    lastInteractionAt = pageLoadedAt;
    signalStep = 0;
    signalPaused = false;
    // Every page reports its own layout once, even when nothing about the
    // device changed between two renders: the reading belongs to THIS render.
    reportedDisplay = null;
    reportedPerformance = false;
    restoredFromHistory = false;
    readScroll();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('scroll', handleScroll, {passive: true});
    // What a person produces and a script does not. `pointermove` covers mouse,
    // pen and touch in one listener; `keydown` catches the reader who pages
    // with the keyboard and never moves the pointer at all. All passive: none
    // of them calls `preventDefault`, and saying so keeps them off the critical
    // path of a scroll.
    INTERACTION_EVENTS.forEach((name) => {
        window.addEventListener(name, noteInteraction, {passive: true});
    });
    // `pagehide` rather than `unload`: a page entering the back/forward cache
    // fires it, and `unload` is ignored outright by mobile browsers.
    window.addEventListener('pagehide', () => post());
    // The counterpart of `pagehide`, and the only way to learn that this
    // document was restored rather than loaded: `persisted` is the browser
    // saying the page came back out of the back/forward cache, where nothing
    // was fetched, parsed or executed and the navigation marks still describe
    // the load that happened before it.
    window.addEventListener('pageshow', (event) => {
        if (event.persisted) {
            restoredFromHistory = true;
        }
    });

    firstSignalTimeout = setTimeout(() => {
        firstSignalTimeout = null;
        // Sent unconditionally, unlike every signal after it: this one answers
        // "did a browser run our script here", and a page nobody touched in its
        // first five seconds still has to answer it.
        post();
        scheduleSignal(nextSpacing());
    }, FIRST_SIGNAL_MS);

    // Published for the template include that loads the Matomo container: the
    // tracker tags in this project are inline scripts in `templates/includes/`
    // and reach the modules through `window`, the same way `window.consentState`
    // and `window.setConsent` are reached from `ga4.html`.
    window.setMatomoTagLoaded = setMatomoTagLoaded;
}
